import { useState } from 'react';

import { useAppSelector } from '@verantos-dev/app-state-base';
import {
  selectCohortDefinition,
  selectIndexDate,
  selectOverallStudyPeriodRestrictionStartDate,
  selectOverallStudyPeriodRestrictionEndDate,
  selectBaselinePeriodRestrictionDaysBeforeIndexDate,
  selectFollowUpPeriodRestrictionDaysAfterIndexDate,
} from '@verantos-dev/app-state-cohort-builder';
import { SubjectVariableCriteriaSelectionTypeLegacy } from '@verantos-dev/types-cohorts';

import { useCheckCohortSummaryForErrors } from '../../../../hooks/common/useCheckCohortSummaryForErrors';
import { useGetAllConceptGroupFromDefinition } from '../../../../hooks/common/useGetAllConceptGroupFromDefinition';

export const useCohortDefinitionSummary = () => {
  const cohortDefinition = useAppSelector(selectCohortDefinition);

  const isIndexDateDefined = !!useAppSelector(selectIndexDate);
  const studyPeriodRestrictionStartDate = useAppSelector(
    selectOverallStudyPeriodRestrictionStartDate
  );
  const studyPeriodRestrictionEndDate = useAppSelector(selectOverallStudyPeriodRestrictionEndDate);
  const daysBeforeIndexDate = useAppSelector(selectBaselinePeriodRestrictionDaysBeforeIndexDate);
  const daysAfterIndexDate = useAppSelector(selectFollowUpPeriodRestrictionDaysAfterIndexDate);

  const [expanded, setExpanded] = useState<number>(0);

  const { conceptGroups } = useGetAllConceptGroupFromDefinition();
  const {
    simpleEventIdsWithNonExistingConcepts,
    eventIdsWithMissingEventNames,
    eventIdsWithDuplicateEventNames,
    inclusionCriteriaIdsWithMissingEventNames,
    inclusionCriteriaIdsWithDuplicateNames,
    exclusionCriteriaIdsWithMissingEventNames,
    exclusionCriteriaIdsWithDuplicateNames,
  } = useCheckCohortSummaryForErrors();

  const inclusionCriteria = cohortDefinition?.subjectVariables.filter(
    (criterion) =>
      criterion.criteriaSelection === SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION
  );

  const exclusionCriteria = cohortDefinition?.subjectVariables.filter(
    (criterion) =>
      criterion.criteriaSelection === SubjectVariableCriteriaSelectionTypeLegacy.EXCLUSION
  );

  const events = [
    ...(cohortDefinition?.simpleEvents || []),
    ...(cohortDefinition?.derivedEvents || []),
  ];

  const handleAccordionChange =
    (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : -1);
    };

  const indexDateCount = +isIndexDateDefined;

  const isDateRangeDefined = Boolean(
    studyPeriodRestrictionStartDate && studyPeriodRestrictionEndDate
  );
  const isBaselinePeriodRestrictionEnabled = Boolean(
    daysBeforeIndexDate || daysBeforeIndexDate === 0
  );
  const isFollowUpPeriodRestrictionEnabled = Boolean(
    daysAfterIndexDate || daysAfterIndexDate === 0
  );

  const studyPeriodCount =
    +isDateRangeDefined + +isBaselinePeriodRestrictionEnabled + +isFollowUpPeriodRestrictionEnabled;

  return {
    inclusionCriteria,
    exclusionCriteria,
    conceptGroups,
    events,
    expanded,

    handleAccordionChange,

    indexDateCount,
    studyPeriodCount,

    simpleEventIdsWithNonExistingConcepts,
    eventIdsWithMissingEventNames,
    eventIdsWithDuplicateEventNames,
    inclusionCriteriaIdsWithMissingEventNames,
    inclusionCriteriaIdsWithDuplicateNames,
    exclusionCriteriaIdsWithMissingEventNames,
    exclusionCriteriaIdsWithDuplicateNames,
  };
};
